import { useQuery } from "@tanstack/react-query";
import { MarketPenetrationData } from "@app/overviews/network-benchmarking-dashboard/tiles/shared";
import { NetworkComparisonFiltersPayload } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters/shared/NetworkComparisonFiltersPayload";
import { BenchmarkingServiceType } from "@app/overviews/network-benchmarking-dashboard/filters/shared/utility";

export interface MarketPenetrationDataResult {
  marketPenetrationData: MarketPenetrationData[];
  marketPenetrationDataLoading: boolean;
}

export const MARKET_PENETRATION_DATA_QUERY_KEY_PREFIX = "benchmarking-market-penetration-data";

// the minimum percentage a bar segment is allowed to display at, so a tiny-but-nonzero value doesn't render as an invisible sliver
const MINIMUM_DISPLAY_PERCENTAGE = 2;

const applyDisplayPercentages = (data: MarketPenetrationData[] | null): MarketPenetrationData[] => {
  for (const x of data ?? []) {
    const roundedMarketOverlapPercentage = Math.round(x.networkMarketOverlapPercentage * 10) / 10;
    const roundedUniverseProviderPercentage = Math.round(x.universeProviderPercentage * 10) / 10;

    x.networkMarketOverlapDisplayPercentage = x.networkMarketOverlapPercentage;
    x.universeProviderDisplayPercentage = x.universeProviderPercentage;

    // if there are values and the rounded percentage rounded to 0, display the unrounded percentage (example: 0.007%)
    if (roundedMarketOverlapPercentage !== 0) {
      x.networkMarketOverlapPercentage = roundedMarketOverlapPercentage;
    }

    if (roundedUniverseProviderPercentage !== 0) {
      x.universeProviderPercentage = roundedUniverseProviderPercentage;
    }

    // if the display value is smaller than the minimum display percentage, then increase the percentage by subtracting
    // however much more is needed to reach the minimum from the other percentage
    if (x.networkMarketOverlapPercentage < MINIMUM_DISPLAY_PERCENTAGE) {
      const amount = MINIMUM_DISPLAY_PERCENTAGE - x.networkMarketOverlapPercentage;
      x.universeProviderDisplayPercentage = x.universeProviderPercentage - amount;
      x.networkMarketOverlapDisplayPercentage = x.networkMarketOverlapPercentage + amount;
    }

    if (x.universeProviderPercentage < MINIMUM_DISPLAY_PERCENTAGE) {
      const amount = MINIMUM_DISPLAY_PERCENTAGE - x.universeProviderPercentage;
      x.networkMarketOverlapDisplayPercentage = x.networkMarketOverlapPercentage - amount;
      x.universeProviderDisplayPercentage = x.universeProviderPercentage + amount;
    }
  }

  return data ?? [];
}

export const useMarketPenetrationData = (
  service: BenchmarkingServiceType,
  payload: NetworkComparisonFiltersPayload | null
): MarketPenetrationDataResult => {
  const queryResult = useQuery<MarketPenetrationData[]>({
    enabled: !!payload,
    queryFn: async ({ signal }) => applyDisplayPercentages(await service.getMarketPenetrationData(payload, signal)),
    queryKey: [MARKET_PENETRATION_DATA_QUERY_KEY_PREFIX, service?.lineOfBusiness, JSON.stringify(payload)],
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  return {
    marketPenetrationData: queryResult.data ?? [],
    marketPenetrationDataLoading: queryResult.isFetching,
  };
}

export default useMarketPenetrationData;
